import { Bot, Download, ListChecks, Paperclip, Sparkles, Star, UserRoundCheck } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge, Button, toast } from '@/components/ui'
import { PausedForCreditsBadge } from '@/features/billing/components/PausedForCredits'
import { useTriageConversationActions, useTriageOverview } from '@/features/triage/queries'
import { priorityVariant } from '@/features/triage/triageFormatting'
import { formatDate, translateEnumValue } from '@/lib/formatting'
import { cn } from '@/lib/utils'

import { AgentModeBadge } from './AgentModeBadge'
import { conversationMediaUrl } from './api'
import { ReplyComposer } from './ReplyComposer'
import { useSetConversationStatus, useTicketAction } from './queries'
import { TicketBanner } from './TicketBanner'
import type { DeskAgentSuggestion, DeskConversationDetail, DeskMessage } from './types'

/**
 * Rotulo do autor de uma mensagem de saida. Quem respondeu importa para o dono: a IA, a equipe pelo Desk, ou ele
 * mesmo pelo celular (Triagem v1). Mensagens do contato nao levam rotulo.
 */
function useAuthorLabel(message: DeskMessage): string | null {
  const { t } = useTranslation()
  switch (message.authorType) {
    case 'agent':
      return t('desk.thread.authors.agent')
    case 'operator':
      return t('desk.thread.authors.operator')
    case 'operator_phone':
      return t('desk.thread.authors.operatorPhone')
    default:
      return null
  }
}

/** Legenda da mídia: o conteúdo chega como "[imagem] legenda" — a etiqueta entre colchetes sai, a legenda fica. */
function mediaCaption(content: string): string {
  return content.replace(/^\[[^\]]+\]\s*/u, '').trim()
}

/**
 * Conteúdo de uma mensagem de mídia. Com binário disponível (hasMedia), renderiza a imagem, o player de áudio/vídeo ou
 * o link de download — sempre pela URL da própria plataforma. Sem binário, ou se o carregamento falhar, volta ao
 * placeholder legível ([áudio], [imagem] legenda...).
 */
function MediaContent({
  message,
  url,
}: {
  readonly message: DeskMessage
  readonly url: string | null
}) {
  const { t } = useTranslation()
  const [failed, setFailed] = useState(false)
  const caption = mediaCaption(message.content)

  const placeholder = (
    <p className="inline-flex items-start gap-1.5 whitespace-pre-wrap break-words italic">
      <Paperclip aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>
        <span className="sr-only">{t('desk.thread.attachment')}: </span>
        {message.content}
      </span>
    </p>
  )

  if (url === null || failed) {
    return placeholder
  }

  switch (message.kind) {
    case 'image':
    case 'sticker':
      return (
        <figure className="flex flex-col gap-1">
          <a href={url} target="_blank" rel="noreferrer" aria-label={t('desk.thread.media.open')}>
            <img
              src={url}
              alt={t('desk.thread.media.imageAlt')}
              loading="lazy"
              className={cn(
                'max-w-full rounded-md object-contain',
                message.kind === 'sticker' ? 'max-h-32' : 'max-h-72',
              )}
              onError={() => {
                setFailed(true)
              }}
            />
          </a>
          {caption !== '' ? (
            <figcaption className="whitespace-pre-wrap break-words">{caption}</figcaption>
          ) : null}
        </figure>
      )
    case 'audio':
      return (
        <audio
          controls
          preload="none"
          src={url}
          className="max-w-full"
          onError={() => {
            setFailed(true)
          }}
        >
          <track kind="captions" />
        </audio>
      )
    case 'video':
      return (
        <figure className="flex flex-col gap-1">
          <video
            controls
            preload="metadata"
            src={url}
            className="max-h-72 max-w-full rounded-md"
            onError={() => {
              setFailed(true)
            }}
          >
            <track kind="captions" />
          </video>
          {caption !== '' ? (
            <figcaption className="whitespace-pre-wrap break-words">{caption}</figcaption>
          ) : null}
        </figure>
      )
    case 'document':
      return (
        <div className="flex flex-col gap-1">
          <a
            href={url}
            download={message.mediaFileName ?? undefined}
            className="inline-flex items-center gap-1.5 font-medium underline underline-offset-2"
          >
            <Download aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
            <span>
              {t('desk.thread.media.download')}:{' '}
              {message.mediaFileName ?? t('desk.thread.media.document')}
            </span>
          </a>
          {caption !== '' ? (
            <p className="whitespace-pre-wrap break-words text-xs">{caption}</p>
          ) : null}
        </div>
      )
    default:
      return placeholder
  }
}

function MessageBubble({
  message,
  language,
  tenantId,
  conversationId,
}: {
  readonly message: DeskMessage
  readonly language: string
  readonly tenantId: string
  readonly conversationId: number
}) {
  const { t } = useTranslation()
  const isInternal = message.visibility === 'internal'
  const isOutgoing = message.direction === 'outgoing'
  const isMedia = message.kind !== 'text'
  const authorLabel = useAuthorLabel(message)

  return (
    <div className={cn('flex', isOutgoing ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[80%] rounded-lg px-3 py-2 text-sm',
          isInternal
            ? 'border border-status-warning/40 bg-status-warning-subtle text-foreground-primary'
            : isOutgoing
              ? 'bg-action-primary text-foreground-on-action'
              : 'bg-background-subtle text-foreground-primary',
        )}
        data-author={message.authorType}
        data-kind={message.kind}
      >
        {isInternal ? (
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-status-warning">
            {t('desk.thread.internalNote')}
          </span>
        ) : authorLabel !== null ? (
          <span
            className={cn(
              'mb-1 block text-[11px] font-semibold uppercase tracking-wide',
              isOutgoing ? 'text-foreground-on-action/80' : 'text-foreground-muted',
            )}
          >
            {authorLabel}
          </span>
        ) : null}

        {isMedia ? (
          <MediaContent
            message={message}
            url={
              message.hasMedia ? conversationMediaUrl(tenantId, conversationId, message.id) : null
            }
          />
        ) : (
          <p className="whitespace-pre-wrap break-words">{message.content}</p>
        )}

        {message.transcript !== null ? (
          <div
            className={cn(
              'mt-2 border-l-2 pl-2 text-xs',
              isOutgoing && !isInternal
                ? 'border-foreground-on-action/40 text-foreground-on-action/90'
                : 'border-border-default text-foreground-secondary',
            )}
          >
            <span className="block text-[11px] font-semibold uppercase tracking-wide">
              {t('desk.thread.transcript')}
            </span>
            <p className="whitespace-pre-wrap break-words">{message.transcript}</p>
          </div>
        ) : null}

        <span
          className={cn(
            'mt-1 block text-[11px]',
            isOutgoing && !isInternal ? 'text-foreground-on-action/80' : 'text-foreground-muted',
          )}
        >
          {formatDate(message.createdAt, language)}
        </span>
      </div>
    </div>
  )
}

function SuggestionCard({
  suggestion,
  language,
}: {
  readonly suggestion: DeskAgentSuggestion
  readonly language: string
}) {
  const { t } = useTranslation()

  // Rotulos localizados do enum de risco vindo da Api ("None"/"Low"/"Medium"/"High"); cai no valor bruto se surgir novo.
  const riskLabels: Readonly<Record<string, string>> = {
    none: t('desk.agent.riskLevels.none'),
    low: t('desk.agent.riskLevels.low'),
    medium: t('desk.agent.riskLevels.medium'),
    high: t('desk.agent.riskLevels.high'),
  }

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border-default bg-background-elevated p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground-secondary">
          <Sparkles aria-hidden="true" className="h-3.5 w-3.5 text-status-info" />
          {t('desk.agent.suggestionTitle')}
        </span>
        {suggestion.autoReplied ? (
          <Badge variant="success">{t('desk.agent.autoReplied')}</Badge>
        ) : suggestion.posted ? (
          <Badge variant="info">{t('desk.agent.postedAsNote')}</Badge>
        ) : (
          <Badge variant="neutral">{t('desk.agent.notPosted')}</Badge>
        )}
      </div>

      {suggestion.suggestedReply !== null ? (
        <p className="whitespace-pre-wrap break-words text-sm text-foreground-primary">
          {suggestion.suggestedReply}
        </p>
      ) : (
        <p className="text-sm text-foreground-muted">{t('desk.agent.noText')}</p>
      )}

      {/* Trace observavel real — sem "AI theater" (docs/design/01 secao 10). */}
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-foreground-muted">
        <div className="flex justify-between gap-2">
          <dt>{t('desk.agent.trace.risk')}</dt>
          <dd className="font-medium text-foreground-secondary">
            {translateEnumValue(riskLabels, suggestion.riskLevel.toLowerCase())}
          </dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt>{t('desk.agent.trace.model')}</dt>
          <dd className="font-medium text-foreground-secondary">{suggestion.trace.model}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt>{t('desk.agent.trace.tokens')}</dt>
          <dd className="font-medium text-foreground-secondary">
            {(suggestion.trace.promptTokens ?? 0) + (suggestion.trace.completionTokens ?? 0)}
          </dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt>{t('desk.agent.trace.duration')}</dt>
          <dd className="font-medium text-foreground-secondary">
            {t('desk.agent.trace.durationValue', { ms: suggestion.trace.durationMs })}
          </dd>
        </div>
      </dl>

      <span className="text-[11px] text-foreground-muted">
        {formatDate(suggestion.createdAt, language)}
      </span>
    </div>
  )
}

/**
 * Card da Triagem na conversa (ADR-028): resumo, prioridade POR CÓDIGO com o motivo e os dois toggles do dono —
 * "não triar esta conversa" e "contato importante". Lê a mesma visão de Pendências (sem endpoint extra); quando a
 * conversa não está em nenhuma das listas, diz que ainda não foi analisada. Nunca cita modelo ou infraestrutura.
 */
function TriageCard({
  tenantId,
  detail,
}: {
  readonly tenantId: string
  readonly detail: DeskConversationDetail
}) {
  const { t } = useTranslation()
  const overview = useTriageOverview(tenantId)
  const actions = useTriageConversationActions(tenantId)
  const item =
    overview.data?.awaitingYou.find((i) => i.conversationSeq === detail.id) ??
    overview.data?.awaitingThem.find((i) => i.conversationSeq === detail.id) ??
    null

  function toggleExclusion(): void {
    actions.exclusion.mutate(
      { conversationSeq: detail.id, excluded: !detail.triageExcluded },
      {
        onSuccess: () => {
          toast.success(t('desk.triageCard.updated'))
        },
        onError: () => {
          toast.error(t('desk.triageCard.actionFailed'))
        },
      },
    )
  }

  function toggleImportant(): void {
    if (detail.contactId === null) {
      return
    }

    actions.important.mutate(
      { contactId: detail.contactId, important: !detail.contactIsImportant },
      {
        onSuccess: () => {
          toast.success(t('desk.triageCard.updated'))
        },
        onError: () => {
          toast.error(t('desk.triageCard.actionFailed'))
        },
      },
    )
  }

  return (
    <div className="mb-4 flex flex-col gap-2 rounded-lg border border-border-default bg-background-elevated p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground-secondary">
          <ListChecks aria-hidden="true" className="h-3.5 w-3.5 text-foreground-muted" />
          {t('desk.triageCard.title')}
        </span>
        {item?.priority !== null && item?.priority !== undefined ? (
          <Badge variant={priorityVariant(item.priority)} title={item.priorityReason ?? undefined}>
            {item.priority}
          </Badge>
        ) : null}
      </div>

      {detail.triageExcluded ? (
        <p className="text-sm text-foreground-muted">{t('desk.triageCard.excluded')}</p>
      ) : item?.summary !== null && item?.summary !== undefined ? (
        <>
          <p className="text-sm text-foreground-primary">{item.summary}</p>
          {item.priorityReason !== null ? (
            <p className="text-xs text-foreground-muted">{item.priorityReason}</p>
          ) : null}
        </>
      ) : (
        <p className="text-sm text-foreground-muted">{t('desk.triageCard.notTriaged')}</p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="secondary"
          loading={actions.exclusion.isPending}
          onClick={toggleExclusion}
        >
          {detail.triageExcluded ? t('desk.triageCard.includeBack') : t('desk.triageCard.exclude')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          loading={actions.important.isPending}
          disabled={detail.contactId === null}
          aria-pressed={detail.contactIsImportant}
          onClick={toggleImportant}
        >
          <Star
            aria-hidden="true"
            className={cn(
              'h-4 w-4',
              detail.contactIsImportant ? 'fill-status-warning text-status-warning' : '',
            )}
          />
          {detail.contactIsImportant
            ? t('desk.triageCard.important')
            : t('desk.triageCard.notImportant')}
        </Button>
      </div>
    </div>
  )
}

interface ConversationViewProps {
  readonly tenantId: string
  readonly detail: DeskConversationDetail
  readonly language: string
}

export function ConversationView({ tenantId, detail, language }: ConversationViewProps) {
  const { t } = useTranslation()
  const status = useSetConversationStatus(tenantId, detail.id)
  const ticketAction = useTicketAction(tenantId, detail.id)
  const isResolved = detail.status.toLowerCase() === 'resolved'

  return (
    <div className="flex h-full flex-col">
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-border-default p-3">
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-foreground-primary">
            {detail.contactName ?? t('desk.list.unknownContact')}
          </span>
          <span className="flex flex-wrap items-center gap-1.5">
            <AgentModeBadge mode={detail.agentMode} />
            <PausedForCreditsBadge tenantId={tenantId} />
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {detail.ticket === null && !isResolved ? (
            // Assumir sem chamado: o atendente pega a conversa da IA (abre um chamado atribuido a si e pausa a IA).
            <Button
              variant="secondary"
              size="sm"
              loading={ticketAction.isPending}
              onClick={() => {
                ticketAction.mutate('take-over', {
                  onSuccess: () => {
                    toast.success(t('desk.ticket.tookOver'))
                  },
                  onError: () => {
                    toast.error(t('desk.ticket.actionFailed'))
                  },
                })
              }}
            >
              <UserRoundCheck aria-hidden="true" className="h-4 w-4" />
              {t('desk.ticket.takeOverConversation')}
            </Button>
          ) : null}
          <Button
            variant="secondary"
            size="sm"
            loading={status.isPending}
            onClick={() => {
              status.mutate(isResolved ? 'reopen' : 'resolve', {
                onSuccess: () => {
                  toast.success(isResolved ? t('desk.header.reopened') : t('desk.header.resolved'))
                },
              })
            }}
          >
            {isResolved ? t('desk.header.reopen') : t('desk.header.resolve')}
          </Button>
        </div>
      </header>

      {detail.ticket !== null ? (
        <TicketBanner tenantId={tenantId} conversationId={detail.id} ticket={detail.ticket} />
      ) : null}

      <div className="grid flex-1 overflow-hidden lg:grid-cols-3">
        <div className="flex min-h-0 flex-col lg:col-span-2">
          <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-3">
            {detail.messages.length === 0 ? (
              <p className="text-sm text-foreground-muted">{t('desk.thread.empty')}</p>
            ) : (
              detail.messages.map((message) => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  language={language}
                  tenantId={tenantId}
                  conversationId={detail.id}
                />
              ))
            )}
          </div>
          <ReplyComposer tenantId={tenantId} conversationId={detail.id} />
        </div>

        <aside className="flex min-h-0 flex-col overflow-y-auto border-t border-border-default p-3 lg:border-l lg:border-t-0">
          <TriageCard tenantId={tenantId} detail={detail} />
          <h3 className="mb-2 inline-flex items-center gap-1.5 text-sm font-semibold text-foreground-primary">
            <Bot aria-hidden="true" className="h-4 w-4 text-foreground-muted" />
            {t('desk.agent.panelTitle')}
          </h3>
          {detail.agentSuggestions.length === 0 ? (
            <p className="text-sm text-foreground-muted">{t('desk.agent.empty')}</p>
          ) : (
            <div className="flex flex-col gap-3">
              {detail.agentSuggestions.map((suggestion) => (
                <SuggestionCard
                  key={suggestion.executionId}
                  suggestion={suggestion}
                  language={language}
                />
              ))}
            </div>
          )}
        </aside>
      </div>
    </div>
  )
}
